import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./TransactionDetailModal.module.css";
import CloseButton from "../Customs/Button/CrossButton";
import {
  downloadInvoicePdf,
  downloadReceiptPdf,
  fetchInvoice,
  fetchReceipt,
  fetchSaleOrder,
} from "../Services/billing";
import { LedgerLink } from "../Models/Models";

export type TransactionDetailTarget = {
  side: "invoice" | "payment";
  link: LedgerLink;
  customerName?: string;
};

interface TransactionDetailModalProps {
  open: boolean;
  target: TransactionDetailTarget | null;
  onClose: () => void;
}

function formatMoney(value: number | string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "₹0.00";
  const num = typeof value === "number" ? value : Number(value);
  if (Number.isNaN(num)) return "₹0.00";
  return `₹${num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(value: unknown): string {
  if (value == null) return "--";
  const raw = String(value).trim();
  if (!raw) return "--";
  const ymd = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (ymd) {
    const d = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
    if (!Number.isNaN(d.getTime())) return d.toLocaleDateString("en-IN");
  }
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? "--" : d.toLocaleDateString("en-IN");
}

function paymentStatusLabel(order: any): string {
  if (!order) return "--";
  const status = String(order?.status || "").toUpperCase();
  if (["CANCELED", "CANCELLED", "REJECTED", "VOIDED"].includes(status)) return "Voided";
  if (status === "DRAFT") return "Checkout";
  const due = Number(order?.due_amount ?? order?.payment_details?.due_amount ?? 0);
  const paid = Number(order?.paid_amount ?? order?.payment_details?.total_paid_amount ?? 0);
  if (due <= 0) return "Paid";
  if (paid > 0) return "Partial Payment";
  return "Pending Payment";
}

function paymentMethodLabel(raw: unknown): string {
  const key = String(raw || "").toUpperCase();
  if (key === "CASH") return "Cash";
  if (key === "UPI") return "UPI";
  if (key === "BANK_TRANSFER") return "Bank Transfer";
  if (key === "REFUND") return "Refund";
  return raw ? String(raw) : "--";
}

const TransactionDetailModal: React.FC<TransactionDetailModalProps> = ({
  open,
  target,
  onClose,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invoice, setInvoice] = useState<any>(null);
  const [order, setOrder] = useState<any>(null);
  const [receipt, setReceipt] = useState<any>(null);
  const [payment, setPayment] = useState<any>(null);

  useEffect(() => {
    if (!open || !target) return;
    let cancelled = false;
    const link = target.link;
    const saleOrderId = link.sale_order_id || undefined;

    setLoading(true);
    setError(null);
    setInvoice(null);
    setOrder(null);
    setReceipt(null);
    setPayment(null);

    const load = async () => {
      try {
        if (target.side === "invoice") {
          let invoiceData: any = null;
          let orderData: any = null;
          const orderIdFromLink = saleOrderId || undefined;
          if (orderIdFromLink) {
            const orderRes: any = await fetchSaleOrder(orderIdFromLink);
            orderData = orderRes?.data || orderRes;
            invoiceData = orderData?.invoice || null;
          } else if (link.invoice_id) {
            const res: any = await fetchInvoice(link.invoice_id);
            invoiceData = res?.data || res;
            const nestedOrderId = invoiceData?.sale_order_id || invoiceData?.order_id;
            if (nestedOrderId) {
              const orderRes: any = await fetchSaleOrder(nestedOrderId);
              orderData = orderRes?.data || orderRes;
            }
          }
          if (cancelled) return;
          setInvoice(invoiceData);
          setOrder(orderData);
          if (!invoiceData && !orderData) {
            setError("Invoice / order details were not found.");
          }
          return;
        }

        let receiptData: any = null;
        if (link.receipt_id) {
          const res: any = await fetchReceipt(link.receipt_id);
          receiptData = res?.data || res;
        }
        const orderId = saleOrderId || receiptData?.sale_order_id;
        let orderData: any = null;
        if (orderId) {
          const orderRes: any = await fetchSaleOrder(orderId);
          orderData = orderRes?.data || orderRes;
        }
        const history: any[] = orderData?.payment_details?.payment_history || [];
        const paymentRow =
          history.find((row) => Number(row.id) === Number(link.payment_id)) || null;
        if (cancelled) return;
        setReceipt(receiptData);
        setOrder(orderData);
        setPayment(paymentRow);
        if (link.kind === "VENDOR_PAYMENT") {
          return;
        }
        if (!receiptData && !paymentRow && !orderData) {
          setError("Payment details were not found.");
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(typeof e === "string" ? e : e?.message || "Failed to load details.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [open, target]);

  if (!open || !target) return null;

  const link = target.link;
  const isInvoice = target.side === "invoice";
  const items: any[] = invoice?.items || invoice?.invoice_items || order?.sale_order_items || [];
  const customerName =
    invoice?.customer_name ||
    invoice?.company_name ||
    receipt?.customer_name ||
    receipt?.company_name ||
    order?.company_name ||
    order?.username ||
    target.customerName ||
    "--";
  const orderId =
    link.sale_order_id || invoice?.sale_order_id || invoice?.order_id || order?.sale_order_id || receipt?.sale_order_id;
  const invoiceId = invoice?.invoice_id || invoice?.id || link.invoice_id;
  const receiptId = receipt?.receipt_id || receipt?.id || link.receipt_id;

  const openOrder = () => {
    if (!orderId) return;
    onClose();
    navigate(`/saleorder/${orderId}`);
  };

  return (
    <div className={styles.overlay} onClick={onClose} role="presentation">
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="txn-detail-title"
      >
        <div className={styles.header}>
          <div>
            <h2 id="txn-detail-title" className={styles.title}>
              {isInvoice ? "Invoice details" : "Payment details"}
            </h2>
            <div className={styles.subtitle}>
              {isInvoice
                ? "Order → Invoice → Items → Amount → Tax → Total → Payment status"
                : "Receipt → Date → Customer → Amount → Method → Related invoice/order"}
            </div>
          </div>
          <CloseButton onClick={onClose} className={styles.closeBtn} />
        </div>

        {loading ? (
          <div className={styles.state}>Loading...</div>
        ) : error ? (
          <div className={styles.error}>{error}</div>
        ) : isInvoice ? (
          <>
            <div className={styles.metaGrid}>
              <div>
                <div className={styles.metaLabel}>Order no.</div>
                <div className={styles.metaValue}>{orderId ? `#${orderId}` : "--"}</div>
              </div>
              <div>
                <div className={styles.metaLabel}>Invoice no.</div>
                <div className={styles.metaValue}>
                  {invoiceId ? `INV-${invoiceId}` : link.number || "Not generated"}
                </div>
              </div>
              <div>
                <div className={styles.metaLabel}>Date</div>
                <div className={styles.metaValue}>
                  {formatDate(invoice?.invoice_date || invoice?.invoice_date_display || order?.order_date)}
                </div>
              </div>
              <div>
                <div className={styles.metaLabel}>Payment status</div>
                <div className={styles.metaValue}>{paymentStatusLabel(order)}</div>
              </div>
            </div>

            <div className={styles.tableWrap}>
              <table className={styles.itemsTable}>
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Qty</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={3}>No line items</td>
                    </tr>
                  ) : (
                    items.map((item: any, idx: number) => (
                      <tr key={item.sale_order_item_id || item.sale_order_items_id || idx}>
                        <td>{item.product_name || item.name || item.door_quality || "--"}</td>
                        <td>{item.quantity ?? item.pcs ?? "--"}</td>
                        <td>{formatMoney(item.amount_inclusive ?? item.line_total ?? item.amount ?? item.total_price)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className={styles.totals}>
              <div className={styles.totalRow}>
                <span>Amount</span>
                <strong>
                  {formatMoney(
                    invoice?.items_amount_inclusive ?? invoice?.subtotal ?? order?.total_amount ?? link.amount
                  )}
                </strong>
              </div>
              <div className={styles.totalRow}>
                <span>Tax</span>
                <strong>{formatMoney(invoice?.tax_amount ?? order?.tax_amount)}</strong>
              </div>
              <div className={`${styles.totalRow} ${styles.totalStrong}`}>
                <span>Total</span>
                <strong>
                  {formatMoney(
                    invoice?.grand_total ?? invoice?.total_cost ?? invoice?.total_amount ?? order?.final_amount ?? link.amount
                  )}
                </strong>
              </div>
            </div>
          </>
        ) : (
          <div className={styles.metaGrid}>
            <div>
              <div className={styles.metaLabel}>Receipt no.</div>
              <div className={styles.metaValue}>
                {receipt?.receipt_number || link.number || (link.payment_id ? `PAY-${link.payment_id}` : "--")}
              </div>
            </div>
            <div>
              <div className={styles.metaLabel}>Payment date</div>
              <div className={styles.metaValue}>
                {formatDate(receipt?.receipt_date || receipt?.paid_at || payment?.paid_at)}
              </div>
            </div>
            <div>
              <div className={styles.metaLabel}>Customer</div>
              <div className={styles.metaValue}>{customerName}</div>
            </div>
            <div>
              <div className={styles.metaLabel}>Amount</div>
              <div className={styles.metaValue}>
                {formatMoney(receipt?.amount || payment?.amount_paid || link.amount)}
              </div>
            </div>
            <div>
              <div className={styles.metaLabel}>Payment method</div>
              <div className={styles.metaValue}>
                {paymentMethodLabel(receipt?.payment_method || receipt?.method || payment?.payment_method)}
              </div>
            </div>
            <div>
              <div className={styles.metaLabel}>Related invoice / order</div>
              <div className={styles.metaValue}>
                {link.invoice_id ? `INV-${link.invoice_id}` : "--"}
                {orderId ? ` · Order #${orderId}` : ""}
              </div>
            </div>
          </div>
        )}

        <div className={styles.footer}>
          {isInvoice && invoiceId ? (
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() =>
                downloadInvoicePdf({
                  invoiceId: Number(invoiceId),
                  saleOrderId: Number(orderId || 0),
                  invoiceNumber: String(invoiceId),
                })
              }
            >
              Download PDF
            </button>
          ) : null}
          {!isInvoice && receiptId ? (
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() =>
                downloadReceiptPdf({
                  receiptId: Number(receiptId),
                  receiptNumber: receipt?.receipt_number,
                })
              }
            >
              Download PDF
            </button>
          ) : null}
          {orderId ? (
            <button type="button" className={styles.secondaryBtn} onClick={openOrder}>
              Open order
            </button>
          ) : null}
          <button type="button" className={styles.primaryBtn} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransactionDetailModal;
